import React, { useState } from 'react';
import { Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, IconButton, MenuItem, MenuList, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { launchColors, overlineSx } from '../launchTheme';
import { YearbookRecipient, YearbookRow } from '../types';
import { CustomerCategory, CustomerContact } from './customerContacts';

export interface ContactCard {
  contact: CustomerContact;
  count: number | null;
}

export const recipientsOf = (row: YearbookRow): YearbookRecipient[] => row.recipients ?? [];

export function contactCards(row: YearbookRow, contacts: CustomerContact[]): ContactCard[] {
  const recipients = recipientsOf(row);
  const entries = recipients.length
    ? recipients.map((recipient) => ({ contactId: recipient.contactId, count: recipient.count as number | null }))
    : row.categoryContactId ? [{ contactId: row.categoryContactId, count: null }] : [];
  return entries.flatMap((entry) => {
    const contact = contacts.find((item) => item.id === entry.contactId);
    return contact ? [{ contact, count: entry.count }] : [];
  });
}

export const countText = (count: number) => (count === 1 ? '1 varuprov' : `${count} varuprover`);

interface Props {
  row: YearbookRow;
  contacts: CustomerContact[];
  canEdit: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSave: (recipients: YearbookRecipient[]) => Promise<boolean>;
  onOpen: (cards: ContactCard[], index: number) => void;
}

const nameSx = (active: boolean, canEdit: boolean) => ({
  flex: 1,
  minWidth: 0,
  justifyContent: 'flex-start',
  borderRadius: 1,
  px: 0.5,
  py: 0.25,
  '&:hover': canEdit || active ? { bgcolor: launchColors.softNavy } : undefined,
});

const CategoryContactCell: React.FC<Props> = ({ row, contacts, canEdit, expanded, onToggle, onSave, onOpen }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const recipients = recipientsOf(row);
  const cards = contactCards(row, contacts);
  const singleId = recipients.length === 1 ? recipients[0].contactId : recipients.length === 0 ? row.categoryContactId || '' : '';
  const single = singleId ? contacts.find((contact) => contact.id === singleId) : undefined;
  const name = single?.name || (singleId ? 'Saknas' : 'Ingen vald');
  const editor = (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      {anchor && (
        <RecipientEditor
          row={row}
          contacts={contacts}
          onCancel={() => setAnchor(null)}
          onSave={async (next) => {
            const saved = await onSave(next);
            if (saved) setAnchor(null);
            return saved;
          }}
        />
      )}
    </Popover>
  );

  if (recipients.length > 1) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
        <ButtonBase
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`Flera mottagare, ${recipients.length}. ${expanded ? 'Dölj' : 'Visa'} mottagarna`}
          sx={nameSx(true, canEdit)}
        >
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy }}>{`Flera mottagare (${recipients.length})`}</Typography>
          {expanded ? <ExpandLessIcon fontSize="small" sx={{ color: launchColors.navy, flexShrink: 0 }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: launchColors.navy, flexShrink: 0 }} />}
        </ButtonBase>
        {canEdit && (
          <Tooltip title="Ändra mottagare">
            <IconButton size="small" aria-label="Ändra mottagare" onClick={(event) => setAnchor(event.currentTarget)} sx={{ color: launchColors.navy }}>
              <EditOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
        {cards.length > 0 && (
          <Tooltip title="Visa kontaktuppgifter">
            <IconButton size="small" aria-label="Kontaktuppgifter för mottagarna" onClick={() => onOpen(cards, 0)} sx={{ color: launchColors.navy }}>
              <ContactPageOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        {editor}
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
      <ButtonBase
        disabled={canEdit ? false : !single}
        onClick={(event) => {
          if (canEdit) setAnchor(event.currentTarget);
          else if (cards.length) onOpen(cards, 0);
        }}
        aria-label={canEdit ? `Välj kategoriansvarig: ${name}` : single ? `Kontaktuppgifter för ${single.name}` : 'Ingen kategoriansvarig'}
        sx={nameSx(false, canEdit)}
      >
        <Typography noWrap sx={{ fontSize: 13, fontWeight: single ? 600 : 400, color: single ? launchColors.navy : launchColors.textSecondary }}>
          {name}
        </Typography>
        {canEdit && <ArrowDropDownIcon fontSize="small" sx={{ color: launchColors.textMuted, flexShrink: 0 }} />}
      </ButtonBase>
      {single && (
        <Tooltip title="Visa kontaktuppgifter">
          <IconButton size="small" aria-label={`Kontaktuppgifter för ${single.name}`} onClick={() => onOpen(cards, 0)} sx={{ color: launchColors.navy }}>
            <ContactPageOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      {editor}
    </Stack>
  );
};

export default CategoryContactCell;

type Draft = { contactId: string; count: string };

const validCount = (value: string) => /^\d{1,5}$/.test(value.trim()) && Number(value) >= 1;

const RecipientEditor: React.FC<{
  row: YearbookRow;
  contacts: CustomerContact[];
  onCancel: () => void;
  onSave: (recipients: YearbookRecipient[]) => Promise<boolean>;
}> = ({ row, contacts, onCancel, onSave }) => {
  const [drafts, setDrafts] = useState<Draft[]>(() => {
    const recipients = recipientsOf(row);
    if (recipients.length) return recipients.map((recipient) => ({ contactId: recipient.contactId, count: String(recipient.count) }));
    return row.categoryContactId ? [{ contactId: row.categoryContactId, count: validCount(row.samples) ? row.samples.trim() : '' }] : [];
  });
  const [choosing, setChoosing] = useState(drafts.length === 0);
  const [focusId, setFocusId] = useState('');
  const [saving, setSaving] = useState(false);
  const nameOf = (contactId: string) => contacts.find((contact) => contact.id === contactId)?.name || 'Saknas';
  const available = contacts.filter((contact) => !drafts.some((draft) => draft.contactId === contact.id));
  const ready = drafts.every((draft) => validCount(draft.count));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(drafts.map((draft) => ({ contactId: draft.contactId, count: Number(draft.count) })));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1.25} sx={{ p: 2, width: 320 }} role="group" aria-label="Mottagare av varuprover">
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: launchColors.navy }}>Mottagare av varuprover</Typography>
      {drafts.map((draft) => (
        <Stack key={draft.contactId} direction="row" spacing={1} alignItems="center">
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: launchColors.navy }}>{nameOf(draft.contactId)}</Typography>
          <TextField
            size="small"
            label="Antal"
            autoFocus={draft.contactId === focusId}
            value={draft.count}
            error={draft.count !== '' && !validCount(draft.count)}
            onChange={(event) => setDrafts((current) => current.map((item) => (item.contactId === draft.contactId ? { ...item, count: event.target.value } : item)))}
            inputProps={{ inputMode: 'numeric', 'aria-label': `Antal varuprover till ${nameOf(draft.contactId)}` }}
            sx={{ width: 88 }}
          />
          <IconButton size="small" aria-label={`Ta bort ${nameOf(draft.contactId)}`} onClick={() => setDrafts((current) => current.filter((item) => item.contactId !== draft.contactId))}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      ))}
      {choosing ? (
        <Box sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '8px', maxHeight: 260, overflowY: 'auto' }}>
          <MenuList dense aria-label="Välj mottagare">
            {contacts.length === 0 && <MenuItem disabled>Inga ansvariga för kedjan</MenuItem>}
            {contacts.length > 0 && available.length === 0 && <MenuItem disabled>Alla är redan valda</MenuItem>}
            {available.map((contact) => (
              <MenuItem
                key={contact.id}
                onClick={() => {
                  setDrafts((current) => [...current, { contactId: contact.id, count: '' }]);
                  setFocusId(contact.id);
                  setChoosing(false);
                }}
              >
                {contact.name}
              </MenuItem>
            ))}
          </MenuList>
        </Box>
      ) : (
        <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} disabled={available.length === 0} onClick={() => setChoosing(true)}>
          Lägg till mottagare
        </Button>
      )}
      {!ready && <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Ange antal varuprover för varje mottagare.</Typography>}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onCancel}>Avbryt</Button>
        <Button size="small" variant="contained" disabled={saving || !ready} onClick={() => { void save(); }}>Spara</Button>
      </Stack>
    </Stack>
  );
};

export const ContactCardsDialog: React.FC<{ cards: ContactCard[]; start: number; onClose: () => void }> = ({ cards, start, onClose }) => {
  const [index, setIndex] = useState(start);
  const current = cards[Math.min(index, cards.length - 1)];
  const contact = current?.contact ?? null;
  const email = contact?.email.trim() || '';
  const phone = contact?.phone.trim() || '';
  const address = contact?.address.trim() || '';
  const note = contact?.note.trim() || '';
  const categories = (contact?.categories || []).filter((category) => category.name.trim() || category.packageMark.trim());
  const phoneHref = phone.replace(/[^\d+]/g, '');
  return (
    <Dialog
      open={Boolean(contact)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="contact-card-title"
      slotProps={{ paper: { sx: { borderRadius: '16px', overflow: 'hidden', bgcolor: launchColors.page } } }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: launchColors.navy, color: '#FFFFFF' }}>
        <Typography sx={{ ...overlineSx, color: 'rgba(255,255,255,0.72)' }}>{contact?.chain}</Typography>
        <Typography id="contact-card-title" component="h2" sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>
          {contact?.name}
        </Typography>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          {current?.count != null && <Fact icon={<Inventory2OutlinedIcon fontSize="small" />} label="Antal varuprover" value={countText(current.count)} />}
          {(email || phone) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: email && phone ? '1fr 1fr' : '1fr' }, gap: 1 }}>
              {email && <Fact icon={<EmailOutlinedIcon fontSize="small" />} label="E-post" value={email} href={`mailto:${email}`} />}
              {phone && <Fact icon={<PhoneOutlinedIcon fontSize="small" />} label="Telefonnummer" value={phone} href={phoneHref ? `tel:${phoneHref}` : undefined} />}
            </Box>
          )}
          {address && <Fact icon={<PlaceOutlinedIcon fontSize="small" />} label="Leveransadress" value={address} />}
          <Box>
            <Typography sx={{ ...overlineSx, mb: 1 }}>Kategorier</Typography>
            {categories.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: launchColors.textSecondary }}>Ingen kategori angiven.</Typography>
            ) : (
              <Stack spacing={1}>
                {categories.map((category, position) => <CategoryCard key={`${category.name}-${category.packageMark}-${position}`} category={category} />)}
              </Stack>
            )}
          </Box>
          {note && (
            <Box sx={{ px: 1.5, py: 1.25, borderRadius: '12px', bgcolor: launchColors.sandLight }}>
              <Typography sx={overlineSx}>Övrig info</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: launchColors.sandDark }}>{note}</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        {cards.length > 1 && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 'auto' }}>
            <Button disabled={index === 0} onClick={() => setIndex((value) => value - 1)}>Föregående</Button>
            <Typography sx={{ fontSize: 14, color: launchColors.textSecondary }}>{`${index + 1} av ${cards.length}`}</Typography>
            <Button disabled={index >= cards.length - 1} onClick={() => setIndex((value) => value + 1)}>Nästa</Button>
          </Stack>
        )}
        <Button onClick={onClose} sx={{ color: launchColors.navy, fontWeight: 700 }}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

const Fact: React.FC<{ icon: React.ReactNode; label: string; value: string; href?: string }> = ({ icon, label, value, href }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, p: 1.25, borderRadius: '12px', bgcolor: launchColors.card, border: `1px solid ${launchColors.divider}` }}>
    <Box sx={{ color: launchColors.navy, display: 'flex', mt: 0.25 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={overlineSx}>{label}</Typography>
      {href ? (
        <Typography component="a" href={href} sx={{ m: 0, mt: 0.25, display: 'block', fontSize: 14, fontWeight: 600, color: launchColors.navy, wordBreak: 'break-word', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {value}
        </Typography>
      ) : (
        <Typography sx={{ m: 0, mt: 0.25, fontSize: 14, fontWeight: 600, color: launchColors.navy, wordBreak: 'break-word' }}>{value}</Typography>
      )}
    </Box>
  </Stack>
);

const CategoryCard: React.FC<{ category: CustomerCategory }> = ({ category }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.25, borderRadius: '12px', bgcolor: launchColors.card, border: `1px solid ${launchColors.divider}` }}>
    <Box sx={{ width: 8, alignSelf: 'stretch', borderRadius: '4px', bgcolor: launchColors.petrol, flexShrink: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: launchColors.navy }}>{category.name.trim() || 'Kategori saknas'}</Typography>
      {category.packageMark.trim() && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <LocalOfferOutlinedIcon sx={{ fontSize: 15, color: launchColors.petrolDark }} />
          <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
            Märk paketet med <Box component="span" sx={{ fontWeight: 700, color: launchColors.text }}>{category.packageMark}</Box>
          </Typography>
        </Stack>
      )}
    </Box>
  </Stack>
);

